import React, { useState, useMemo, useCallback } from 'react';
import { X, Save, Send, Loader2, Bot, User } from 'lucide-react';
import type { DatasheetFrontmatter } from '@/services/datasheetService';
import type { IPartCodeCreateRequest } from '@/types/psi';
import { usePartCodeCategories, useCreatePartCode, useCreatePart } from '@/hooks/usePSI';
import { correctFootprintStream, saveExtractedToDrive, type ChatMessage, type ExtractProgressEvent } from '@/services/datasheetService';
import FootprintPreview from './FootprintPreview';

interface PartCreateReviewProps {
  frontmatter: DatasheetFrontmatter | null;
  footprint: Record<string, unknown> | null;
  footprintPng?: string | null;
  sourceFilename: string;
  sourceFileId?: string;
  userHint?: string;
  initialTypeCode?: string;
  initialPartNumber?: string;
  onComplete: () => void;
  onBack: () => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Frontmatter → Part Code mapping
// ---------------------------------------------------------------------------

interface ICategoryInfo { id: string; name: string; sheet_name: string; }

function mapFrontmatterToPartCode(fm: DatasheetFrontmatter | null): Partial<IPartCodeCreateRequest> {
  if (!fm) return {};
  const comp = fm.component || {};
  const manufacturer = comp.manufacturer || fm.manufacturer || '';
  const partNumber = comp.part_number || fm.part_number || '';
  const pkg = comp.package || fm.package || '';
  const category = comp.category || fm.category || '';
  let spec = fm.description || '';
  if (!spec && fm.electrical_characteristics?.length) {
    const items = fm.electrical_characteristics.slice(0, 3);
    spec = items.map(ec => {
      const parts = [ec.param, ec.description].filter(Boolean).join(' ');
      const values = [ec.typ ?? ec.max ?? ec.min, ec.unit].filter(v => v != null).join('');
      return parts ? `${parts}: ${values}` : '';
    }).filter(Boolean).join('; ');
  }
  return { brand: manufacturer, vendor_product: partNumber, vendor_spec: spec, dimension: pkg, category };
}

function guessCategory(fm: DatasheetFrontmatter | null, categories: ICategoryInfo[]): string {
  if (!categories.length) return '';
  const cat = (fm?.component?.category || fm?.category || '').toLowerCase();
  const keywords: Record<string, string[]> = {
    electronic_parts: ['ic', 'transistor', 'mosfet', 'diode', 'resistor', 'capacitor', 'inductor', 'led', 'sensor', 'opamp', 'regulator', 'mcu', 'memory', 'power'],
    electronic_modules: ['module', 'board'],
    connectors: ['connector', 'header', 'socket', 'terminal'],
  };
  for (const [catId, kws] of Object.entries(keywords)) {
    if (kws.some(kw => cat.includes(kw))) {
      const match = categories.find(c => c.id === catId);
      if (match) return match.id;
    }
  }
  return categories[0]?.id || '';
}

// ---------------------------------------------------------------------------
// Chat message component
// ---------------------------------------------------------------------------

const ChatBubble: React.FC<{ msg: ChatMessage; isStreaming?: boolean }> = ({ msg, isStreaming }) => (
  <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    {msg.role === 'assistant' && <Bot className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />}
    <div className={`max-w-[85%] px-3 py-1.5 rounded-lg text-xs ${
      msg.role === 'user'
        ? 'bg-blue-600 text-white'
        : 'bg-gray-700 text-gray-200'
    }`}>
      {msg.content}
      {isStreaming && <Loader2 className="w-3 h-3 animate-spin inline-block ml-1" />}
    </div>
    {msg.role === 'user' && <User className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PartCreateReview: React.FC<PartCreateReviewProps> = ({
  frontmatter, footprint: initialFootprint, footprintPng, sourceFilename, sourceFileId,
  userHint, initialTypeCode, initialPartNumber, onComplete, onBack, onCancel,
}) => {
  const { data: categories = [] } = usePartCodeCategories();
  const createMutation = useCreatePartCode();
  const createPartMutation = useCreatePart();

  const initialFields = useMemo(() => {
    const mapped = mapFrontmatterToPartCode(frontmatter);
    // Pre-fill category and part_number from bbox step if available
    if (initialTypeCode) mapped.category = initialTypeCode;
    if (initialPartNumber) mapped.part_number = initialPartNumber;
    return mapped;
  }, [frontmatter, initialTypeCode, initialPartNumber]);
  const initialCategory = useMemo(() => {
    // If a type code was selected in bbox step, it's from electronic_parts
    if (initialTypeCode) return 'electronic_parts';
    return guessCategory(frontmatter, categories);
  }, [frontmatter, categories, initialTypeCode]);

  const [categoryId, setCategoryId] = useState(initialCategory);
  const [fields, setFields] = useState<Partial<IPartCodeCreateRequest>>(initialFields);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live footprint state (updated by chat corrections)
  const [footprint, setFootprint] = useState<Record<string, unknown> | null>(initialFootprint);

  // Chat state — seed with user hint if provided
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => {
    const msgs: ChatMessage[] = [];
    if (userHint) {
      msgs.push({ role: 'user', content: userHint });
    }
    msgs.push({ role: 'assistant', content: 'Footprint extracted. Review the preview and type corrections if needed.' });
    return msgs;
  });
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  // Sync fields when frontmatter arrives
  React.useEffect(() => {
    const mapped = mapFrontmatterToPartCode(frontmatter);
    if (Object.keys(mapped).length > 0) setFields(prev => ({ ...prev, ...mapped }));
  }, [frontmatter]);

  React.useEffect(() => {
    if (categories.length) {
      const guessed = guessCategory(frontmatter, categories);
      if (guessed) setCategoryId(guessed);
    }
  }, [categories, frontmatter]);

  // Scroll chat to bottom
  React.useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const updateField = (key: keyof IPartCodeCreateRequest, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!categoryId) { setError('Please select a category'); return; }
    setSaving(true); setError(null);
    try {
      // Write to Firestore parts master (Phase 1e — primary)
      try {
        await createPartMutation.mutateAsync({
          description: fields.vendor_product || fields.brand || '',
          part_type: fields.category || '',     // type code like "R", "C" — resolved by backend
          package_size: fields.dimension || '',
          category: categoryId,
          specification: fields.vendor_spec || '',
          brand: fields.brand || '',
          manufacturer_pn: fields.vendor_product || '',
          datasheet_file_id: sourceFileId || undefined,
        });
      } catch (partErr) {
        console.warn('[PartCreate] Failed to write to parts master:', partErr);
        // Non-fatal during dual-write period — Sheets write continues below
      }

      // Write to Google Sheets (legacy — keep during transition)
      await createMutation.mutateAsync({ categoryId, data: fields as IPartCodeCreateRequest });

      // Save extracted.json to Drive alongside the original PDF (best-effort)
      try {
        const extracted: Record<string, unknown> = {
          source_filename: sourceFilename,
          extracted_at: new Date().toISOString(),
          part_code: {
            category_sheet: categoryId,
            type_code: fields.category,
            part_number: fields.part_number,
            brand: fields.brand,
            vendor_product: fields.vendor_product,
            vendor_spec: fields.vendor_spec,
            dimension: fields.dimension,
          },
          frontmatter: frontmatter,
          footprint: footprint,
          user_prompts: chatMessages
            .filter(m => m.role === 'user')
            .map(m => m.content),
        };
        await saveExtractedToDrive(sourceFileId, sourceFilename, extracted, footprint, footprintPng);
      } catch (saveErr) {
        console.warn('[PartCreate] Failed to save extraction to Drive:', saveErr);
      }

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create part code');
    } finally { setSaving(false); }
  };

  // Chat correction handler
  const handleSendCorrection = useCallback(async () => {
    if (!chatInput.trim() || !footprint || chatLoading) return;
    const prompt = chatInput.trim();
    setChatInput('');

    // Add user message
    const userMsg: ChatMessage = { role: 'user', content: prompt };
    setChatMessages(prev => [...prev, userMsg]);
    setChatLoading(true);

    // Add placeholder assistant message
    const assistantMsg: ChatMessage = { role: 'assistant', content: 'Correcting...' };
    setChatMessages(prev => [...prev, assistantMsg]);

    try {
      const result = await correctFootprintStream(
        prompt,
        footprint,
        chatMessages.filter(m => m.role !== 'assistant' || m.content !== 'Correcting...'),
        (_progress: ExtractProgressEvent) => {
          // Update streaming message
          setChatMessages(prev => {
            const msgs = [...prev];
            msgs[msgs.length - 1] = { role: 'assistant', content: _progress.message };
            return msgs;
          });
        },
      );

      // Update footprint with corrected data
      setFootprint(prev => ({
        ...prev,
        footprint: result.footprint,
        dimensions: result.dimensions,
      }));

      // Update assistant message
      setChatMessages(prev => {
        const msgs = [...prev];
        msgs[msgs.length - 1] = { role: 'assistant', content: result.message || 'Footprint updated.' };
        return msgs;
      });
    } catch (err) {
      setChatMessages(prev => {
        const msgs = [...prev];
        msgs[msgs.length - 1] = { role: 'assistant', content: `Error: ${err instanceof Error ? err.message : 'Correction failed'}` };
        return msgs;
      });
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, footprint, chatLoading, chatMessages]);

  const fieldConfig = [
    { key: 'brand' as const, label: 'Brand / Manufacturer', labelZh: '廠牌' },
    { key: 'vendor_name' as const, label: 'Vendor Name', labelZh: '廠商名稱' },
    { key: 'vendor_product' as const, label: 'Vendor Part Number', labelZh: '廠商品名' },
    { key: 'vendor_spec' as const, label: 'Specification', labelZh: '廠商規格' },
    { key: 'dimension' as const, label: 'Package / Dimension', labelZh: '尺寸' },
  ];

  const hasFootprint = !!footprint;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl m-4 flex flex-col ${
        hasFootprint ? 'max-w-6xl w-full h-[90vh]' : 'max-w-6xl w-full h-[90vh]'
      } overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review Extracted Data</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {sourceFilename} — Review and edit before saving
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — two-column layout when footprint exists */}
        <div className={`flex-1 overflow-auto ${hasFootprint ? 'flex' : ''}`}>
          {/* Left: Form fields */}
          <div className={`p-4 space-y-3 overflow-auto ${hasFootprint ? 'w-[320px] shrink-0 border-r border-gray-200 dark:border-gray-700' : 'w-full'}`}>
            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Part Code Category</label>
              <select value={categoryId} onChange={e => setCategoryId(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs">
                <option value="">Select...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Fields */}
            {fieldConfig.map(({ key, label, labelZh }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {label} <span className="text-gray-400 text-[10px]">({labelZh})</span>
                </label>
                <input type="text" value={fields[key] || ''} onChange={e => updateField(key, e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs" />
              </div>
            ))}

            {error && (
              <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1.5 rounded-lg">{error}</div>
            )}
          </div>

          {/* Right: Footprint split view + chat */}
          {hasFootprint && (
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {/* Split view: PNG reference | Footprint canvas */}
              <div className="flex-1 flex min-h-0">
                {/* Left: Cropped PNG reference */}
                <div className="w-1/2 border-r border-gray-700 bg-gray-900 flex flex-col">
                  <div className="px-2 py-1 text-[10px] text-gray-400 border-b border-gray-700 font-medium">
                    Reference Image
                  </div>
                  <div className="flex-1 flex items-center justify-center p-2 overflow-hidden">
                    {footprintPng ? (
                      <img
                        src={`data:image/png;base64,${footprintPng}`}
                        alt="Dimensional drawing"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs text-gray-500">No reference image</span>
                    )}
                  </div>
                </div>

                {/* Right: Footprint canvas */}
                <div className="w-1/2 flex flex-col min-w-0">
                  <div className="px-2 py-1 text-[10px] text-gray-400 border-b border-gray-200 dark:border-gray-700 font-medium">
                    Footprint Preview
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <FootprintPreview
                      footprint={(footprint as Record<string, unknown>)?.footprint as any}
                      dimensions={(footprint as Record<string, unknown>)?.dimensions as any}
                      rawJson={footprint as Record<string, unknown>}
                      height={280}
                      editable
                      onFootprintChange={() => {}}
                    />
                  </div>
                </div>
              </div>

              {/* Chat: Correction input */}
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex flex-col" style={{ height: 160 }}>
                {/* Messages */}
                <div className="flex-1 overflow-auto px-3 py-2 space-y-2">
                  {chatMessages.map((msg, i) => (
                    <ChatBubble key={i} msg={msg} isStreaming={chatLoading && i === chatMessages.length - 1 && msg.role === 'assistant'} />
                  ))}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendCorrection(); } }}
                    placeholder="Describe correction (e.g., 'body should be 3.5x3.0mm')..."
                    disabled={chatLoading}
                    className="flex-1 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSendCorrection}
                    disabled={chatLoading || !chatInput.trim()}
                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1 text-xs"
                  >
                    {chatLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button onClick={onBack} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Back
          </button>
          <button onClick={handleSave} disabled={saving || !categoryId}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Part Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartCreateReview;
